import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import './Toast.css';

export type ToastVariant = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
  leaving?: boolean;
}

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  dismiss: (id: number) => void;
}

const TOAST_MS = 4000;

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Tiny module-level bridge so non-hook callers (rare) can fire toasts.
 * Set by ToastProvider on mount. If no provider is mounted, pushes are
 * silently dropped — the UI still works, just without the notification.
 */
let externalPush: ((message: string, variant: ToastVariant) => void) | null = null;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    // Mark leaving, then remove after the CSS out-transition finishes.
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    const t = timers.current.get(id);
    if (t) { clearTimeout(t); timers.current.delete(id); }
    const out = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 220);
    timers.current.set(id, out);
  }, []);

  const push = useCallback(
    (message: string, variant: ToastVariant) => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, message, variant }]);
      const t = setTimeout(() => dismiss(id), TOAST_MS);
      timers.current.set(id, t);
    },
    [dismiss],
  );

  useEffect(() => {
    externalPush = push;
    return () => {
      externalPush = null;
      for (const t of timers.current.values()) clearTimeout(t);
      timers.current.clear();
    };
  }, [push]);

  const api = useMemo<ToastApi>(
    () => ({
      success: (m) => push(m, 'success'),
      error: (m) => push(m, 'error'),
      info: (m) => push(m, 'info'),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast toast--${t.variant} ${t.leaving ? 'toast--leaving' : ''}`}
            onClick={() => dismiss(t.id)}
          >
            <span className="toast__dot" aria-hidden="true" />
            <span className="toast__msg">{t.message}</span>
            <button
              type="button"
              className="toast__close"
              onClick={(e) => { e.stopPropagation(); dismiss(t.id); }}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Hook for firing toasts from components. If no provider is mounted,
 * returns a no-op so callers don't crash during tests / isolated renders.
 */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (ctx) return ctx;
  return {
    success: (m) => externalPush?.(m, 'success'),
    error: (m) => externalPush?.(m, 'error'),
    info: (m) => externalPush?.(m, 'info'),
    dismiss: () => {},
  };
}

/**
 * Module-level helper for edge cases where a hook can't be used (e.g.
 * inside a non-React util). Silently drops if no provider is mounted.
 */
export const toast = {
  success: (m: string) => externalPush?.(m, 'success'),
  error: (m: string) => externalPush?.(m, 'error'),
  info: (m: string) => externalPush?.(m, 'info'),
};
